
import { useTranslation } from 'react-i18next';
import { X, MessageCircle, Smartphone, Mail, Copy, Share2, Send } from 'lucide-react';

interface InviteModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function InviteModal({ isOpen, onClose }: InviteModalProps) {
    const { t } = (useTranslation as any)(['common']);
    if (!isOpen) return null;

    const shareUrl = `${window.location.origin}/install`;
    const shareText = t('invite.share_text');

    const shareTo = (platform: string) => {
        const textEncoded = encodeURIComponent(shareText);
        const urlEncoded = encodeURIComponent(shareUrl);

        let link = '';
        switch (platform) {
            case 'whatsapp':
                link = `https://wa.me/?text=${textEncoded}%20${urlEncoded}`;
                break;
            case 'facebook':
                // Use quote parameter for better pre-filling on FB
                link = `https://www.facebook.com/sharer/sharer.php?u=${urlEncoded}&quote=${textEncoded}`;
                break;
            case 'x':
                link = `https://twitter.com/intent/tweet?url=${urlEncoded}&text=${textEncoded}`;
                break;
            case 'snapchat':
                link = `https://www.snapchat.com/share?url=${urlEncoded}&text=${textEncoded}`;
                break;
            case 'tiktok':
            case 'instagram':
                if (navigator.share) {
                    navigator.share({ title: 'PadelPartner', text: shareText, url: shareUrl })
                        .catch(err => {
                            console.error('Share failed', err);
                            shareTo('copy');
                        });
                    return;
                }
                shareTo('copy');
                return;
            case 'sms':
                link = `sms:&body=${textEncoded}%20${urlEncoded}`;
                break;
            case 'email':
                link = `mailto:?subject=${encodeURIComponent(t('invite.email_subject'))}&body=${textEncoded}%20${urlEncoded}`;
                break;
            case 'telegram':
                link = `https://t.me/share/url?url=${urlEncoded}&text=${textEncoded}`;
                break;
            case 'native':
                if (navigator.share) {
                    navigator.share({ title: t('common:logo', { defaultValue: 'PadelPartner' }), text: shareText, url: shareUrl }).catch(console.error);
                } else {
                    alert(t('invite.native_not_supported'));
                }
                return;
            case 'copy':
                const fullText = `${shareText} ${shareUrl}`;
                if (navigator.clipboard) {
                    navigator.clipboard.writeText(fullText)
                        .then(() => alert(t('invite.link_copied')))
                        .catch(() => prompt(t('invite.copy_link_prompt'), fullText));
                } else {
                    prompt(t('invite.copy_link_prompt'), fullText);
                }
                return;
        }

        if (link) {
            try {
                window.open(link, '_blank');
            } catch (e) {
                console.error('Window open failed', e);
                shareTo('copy');
            }
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={onClose}>
            <div
                className="bg-white w-full max-w-sm rounded-[2rem] p-6 shadow-2xl animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-black text-slate-800">{t('invite.title')}</h3>
                    <button
                        onClick={onClose}
                        className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition"
                    >
                        <X size={20} className="text-slate-500" />
                    </button>
                </div>

                <div className="grid grid-cols-4 gap-4 mb-6">
                    <button onClick={() => shareTo('whatsapp')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-[#25D366]/10 text-[#25D366] rounded-2xl flex items-center justify-center group-active:scale-95 transition">
                            <MessageCircle size={28} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">WhatsApp</span>
                    </button>
                    <button onClick={() => shareTo('facebook')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-[#1877F2]/10 rounded-2xl flex items-center justify-center group-active:scale-95 transition">
                            <img src="/FACEBOOK-Photoroom.png" alt="Facebook" className="w-8 h-8 object-contain" />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">Facebook</span>
                    </button>
                    <button onClick={() => shareTo('instagram')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-[#E4405F]/10 rounded-2xl flex items-center justify-center group-active:scale-95 transition">
                            <img src="/INSTAGRAM-Photoroom.png" alt="Instagram" className="w-8 h-8 object-contain" />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">Instagram</span>
                    </button>
                    <button onClick={() => shareTo('x')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-black/5 rounded-2xl flex items-center justify-center group-active:scale-95 transition">
                            <img src="/X-Photoroom.png" alt="X" className="w-7 h-7 object-contain" />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">X</span>
                    </button>
                    <button onClick={() => shareTo('tiktok')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-black/5 rounded-2xl flex items-center justify-center group-active:scale-95 transition">
                            <img src="/TIKTOK-Photoroom.png" alt="TikTok" className="w-7 h-7 object-contain" />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">TikTok</span>
                    </button>
                    <button onClick={() => shareTo('snapchat')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-[#FFFC00]/10 rounded-2xl flex items-center justify-center group-active:scale-95 transition border border-yellow-200">
                            {/* Fallback to text/yellow square if image still failing, or we create a simple div icon */}
                            <div className="w-7 h-7 bg-[#FFFC00] border border-black/5 rounded-md flex items-center justify-center">
                                <span className="text-[8px] font-black text-black">SNAP</span>
                            </div>
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">Snapchat</span>
                    </button>
                    <button onClick={() => shareTo('sms')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center group-active:scale-95 transition">
                            <Smartphone size={28} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">{t('invite.platforms.sms')}</span>
                    </button>
                    <button onClick={() => shareTo('email')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-slate-100 text-slate-600 rounded-2xl flex items-center justify-center group-active:scale-95 transition">
                            <Mail size={28} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">{t('invite.platforms.email')}</span>
                    </button>
                    <button onClick={() => shareTo('telegram')} className="flex flex-col items-center gap-2 group">
                        <div className="w-14 h-14 bg-[#0088cc]/10 text-[#0088cc] rounded-2xl flex items-center justify-center group-active:scale-95 transition">
                            <Send size={28} />
                        </div>
                        <span className="text-[10px] font-bold text-slate-600 truncate w-full text-center">{t('invite.platforms.telegram')}</span>
                    </button>
                </div>

                <div className="space-y-3">
                    <button
                        onClick={() => shareTo('copy')}
                        className="w-full py-4 bg-slate-50 hover:bg-slate-100 rounded-xl flex items-center justify-center gap-3 font-bold text-slate-700 transition"
                    >
                        <Copy size={20} />
                        {t('invite.copy_link_action')}
                    </button>
                    {typeof navigator.share === 'function' && (
                        <button
                            onClick={() => shareTo('native')}
                            className="w-full py-4 bg-slate-900 text-white rounded-xl flex items-center justify-center gap-3 font-bold shadow-lg shadow-slate-900/20 active:scale-95 transition"
                        >
                            <Share2 size={20} />
                            {t('invite.other_options')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
